import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { maskMoneyInput, parseInputToApi, moneyToInput } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';
import type { AlertRule, TriggerInfo } from './types';

/**
 * Cadastro de regra de alerta — RF-ALE-02.
 *
 * O formulário mostra APENAS o parâmetro do gatilho escolhido. Exibir os três
 * campos sempre — antecedência, valor mínimo e percentual — deixaria o usuário
 * preenchendo o que o gatilho ignora, e a regra sairia diferente do que ele
 * acha que configurou.
 */
export function AlertRuleForm({
  rule,
  triggers,
  selectedIds,
  onClose,
  onSaved,
}: {
  rule: AlertRule | null;
  triggers: TriggerInfo[];
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [trigger, setTrigger] = useState(rule?.trigger ?? triggers[0]?.id ?? 'conta_vencendo');
  const [name, setName] = useState(rule?.name ?? '');
  const [entityId, setEntityId] = useState<string>(rule?.entity_id?.toString() ?? '');
  const [severity, setSeverity] = useState(rule?.severity ?? 'aviso');
  const [frequency, setFrequency] = useState(rule?.frequency ?? 'uma_vez_por_periodo');
  const [active, setActive] = useState(rule?.active ?? true);

  const [daysBefore, setDaysBefore] = useState(rule?.days_before?.toString() ?? '3');
  const [amount, setAmount] = useState(
    rule?.amount_threshold ? moneyToInput(rule.amount_threshold) : '',
  );
  const [percentage, setPercentage] = useState(rule?.percentage_threshold?.toString() ?? '');
  const [error, setError] = useState<string | null>(null);

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  const info = triggers.find((t) => t.id === trigger);

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        trigger,
        entity_id: entityId === '' ? null : Number(entityId),
        active,
        severity,
        frequency,
        // Os três vão sempre, e os que não servem vão NULOS. Omitir deixaria o
        // valor antigo gravado ao trocar o gatilho de uma regra existente.
        days_before: info?.param === 'days_before' && daysBefore !== '' ? Number(daysBefore) : null,
        amount_threshold:
          info?.param === 'amount_threshold' && amount !== '' ? parseInputToApi(amount) : null,
        percentage_threshold:
          info?.param === 'percentage_threshold' && percentage !== '' ? Number(percentage) : null,
      };

      return rule
        ? api.put(`/alert-rules/${rule.id}`, payload, { entityIds: selectedIds })
        : api.post('/alert-rules', payload, { entityIds: selectedIds });
    },
    onSuccess: onSaved,
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'Não foi possível salvar a regra.'),
  });

  const entities = reference.data?.entities ?? [];

  return (
    <Modal title={rule ? 'Editar regra de alerta' : 'Nova regra de alerta'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
        className="space-y-3.5"
      >
        <Field label="Sobre o que avisar" required>
          <select
            value={trigger}
            onChange={(e) => {
              setTrigger(e.target.value);
              // Nome automático só enquanto o usuário não escreveu o seu.
              const next = triggers.find((t) => t.id === e.target.value);
              if (next && (name === '' || triggers.some((t) => t.label === name))) {
                setName(next.label);
              }
            }}
            className="input w-full"
          >
            {triggers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>

        {info && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
            {info.hint}
          </p>
        )}

        <Field label="Nome da regra" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={180}
            placeholder="Contas vencendo em 3 dias"
            className="input w-full"
          />
        </Field>

        {/* --- Parâmetro do gatilho, só o que se aplica --- */}
        {info?.param === 'days_before' && (
          <Field label="Antecedência (dias)" required hint="0 avisa no próprio dia do vencimento.">
            <input
              type="number"
              value={daysBefore}
              onChange={(e) => setDaysBefore(e.target.value)}
              min={0}
              max={365}
              required
              className="input w-full"
            />
          </Field>
        )}

        {info?.param === 'amount_threshold' && (
          <Field label="Avisar quando ficar abaixo de" required>
            <input
              value={amount}
              onChange={(e) => setAmount(maskMoneyInput(e.target.value))}
              inputMode="decimal"
              placeholder="R$ 0,00"
              required
              className="input w-full"
            />
          </Field>
        )}

        {info?.param === 'percentage_threshold' && (
          <Field
            label="Percentual"
            hint={
              trigger === 'orcamento_limiar'
                ? 'Deixe em branco para receber 70%, 90% e 100%.'
                : 'Padrão: 80%.'
            }
          >
            <input
              type="number"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              min={0}
              max={1000}
              step="0.1"
              placeholder={trigger === 'orcamento_limiar' ? 'todos os limiares' : '80'}
              className="input w-full"
            />
          </Field>
        )}

        <Field
          label="Entidade"
          hint="Em branco vale para todas — inclusive as que você criar depois."
        >
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="input w-full">
            <option value="">Todas as entidades</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Gravidade">
            <select value={severity} onChange={(e) => setSeverity(e.target.value as never)} className="input w-full">
              <option value="info">Informação</option>
              <option value="aviso">Aviso</option>
              <option value="critico">Crítico</option>
            </select>
          </Field>

          <Field label="Repetição">
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="input w-full">
              <option value="uma_vez_por_periodo">Uma vez por mês</option>
              <option value="diaria">Todo dia</option>
            </select>
          </Field>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
          />
          Regra ativa
        </label>

        {error && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending || name.trim() === ''}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
